import { fetchViaProxy } from '@/actions/proxy-fetch';
import Avatar from '@/components/next-ui/Avatar';
import Input from '@/components/next-ui/Input';
import Typography from '@/components/next-ui/Typography';
import useDebounce from '@/hooks/useDebounce';
import Spinner from '@components/Spinner';
import { Combobox } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import { cx } from 'cva';
import { isArray, isEmpty, isNull } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { MdClose } from 'react-icons/md';

const searchCompanies = async (searchText) => {
  if (isEmpty(searchText)) {
    return [];
  }
  const { ok, data } = await fetchViaProxy(
    `/proxy/v2/protected/users/companies?search=${searchText}&limit=10`,
    {
      method: 'GET',
    },
  );

  if (!ok) {
    throw new Error('Something went wrong');
  }
  return data.data || [];
};

function CompanySearchBox(props) {
  const { onChange, multiple, error, initialValue } = props;

  const [value, setValue] = useState(multiple ? [] : null);
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText, 250) || '';

  const { data, isLoading } = useQuery({
    queryKey: [`search-companies`, debouncedSearchText],
    queryFn: async () => {
      return searchCompanies(debouncedSearchText);
    },
    keepPreviousData: false,
  });

  const companies = isArray(data)
    ? data.filter((item) => {
        if (multiple && isArray(value)) {
          return !value.some((v) => v.id === item.id);
        }
        return value?.id !== item.id;
      })
    : [];

  const isSearching = isLoading || debouncedSearchText !== searchText;

  useEffect(() => {
    if (!isEmpty(initialValue)) {
      const queryParams = new URLSearchParams();
      queryParams.append('ids', initialValue.join(','));
      fetchViaProxy(
        `/proxy/v2/protected/users/companies?${queryParams.toString()}`,
        {
          method: 'GET',
        },
      ).then(({ ok, data }) => {
        if (ok && data?.data) {
          setValue(data.data);
        }
      });
    }
  }, []);

  const handleValueChange = (selected) => {
    setValue(selected);
    onChange(selected);
  };

  const handleRemoveSelection = (company) => {
    const filtered = value.filter((v) => v.id !== company.id);
    handleValueChange(filtered);
  };

  return (
    <Combobox
      as="div"
      multiple={multiple}
      className="w-full space-y-2"
      onChange={(selected) => {
        handleValueChange(selected);
      }}
      value={value}>
      {({ open }) => (
        <>
          <div className="relative">
            <div className="flex w-full cursor-default flex-wrap overflow-visible rounded-lg bg-primary-light-1">
              {multiple && value?.length > 0 && (
                <div className="flex flex-wrap items-center justify-start gap-4 px-2 py-2">
                  {value?.map((company) => (
                    <div
                      key={company.id}
                      className="relative rounded-md bg-chips-primary">
                      <Typography
                        variant="body2"
                        className="truncate px-2 py-1">
                        {company.name}
                      </Typography>
                      <button
                        className="p-0.25 absolute right-0 top-0 -translate-y-1.5 translate-x-2 transform rounded-full bg-white"
                        onClick={() => handleRemoveSelection(company)}>
                        <MdClose className="h-4 w-4 text-secondary" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <Combobox.Input
                as={Input}
                required
                displayValue={(company) => company?.name || ''}
                placeholder="Search Communities..."
                className="w-full flex-1"
                error={error}
                onChange={(e) => {
                  setSearchText(e.target.value);
                }}
              />
            </div>
            {open && !isNull(companies) && (
              <Combobox.Options
                as="ul"
                className="absolute z-10 mt-2 max-h-52 w-full overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg">
                {isSearching && (
                  <div className="relative flex w-full items-center justify-start gap-2 px-4 py-2">
                    <Spinner color="primary" size="xs" />
                    <Typography variant="body1" color="textSecondary">
                      Searching...
                    </Typography>
                  </div>
                )}
                {isEmpty(companies) && !isSearching && (
                  <div className="relative flex w-full items-center justify-start p-2">
                    <Typography variant="body1">
                      No communities found
                    </Typography>
                  </div>
                )}
                {companies?.map((company) => (
                  <Combobox.Option
                    key={company.id}
                    value={company}
                    className={({ active }) =>
                      cx(
                        'relative cursor-default select-none py-2 pl-4 pr-4 text-text-primary',
                        active && 'bg-primary-light-1',
                      )
                    }>
                    {({ selected, active }) => (
                      <div className="flex items-center justify-start gap-4">
                        <Avatar
                          alt={company.name}
                          src={company.logoUrl}
                          size="xs"
                          profile="company"
                        />
                        <Typography variant="body2" className="truncate">
                          {company.name}
                        </Typography>
                        {/* {selected ? (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                            <AiOutlineCheck
                              className="h-5 w-5 text-text-primary"
                              aria-hidden="true"
                            />
                          </span>
                        ) : null} */}
                      </div>
                    )}
                  </Combobox.Option>
                ))}
              </Combobox.Options>
            )}
          </div>
        </>
      )}
    </Combobox>
  );
}

export default CompanySearchBox;
